import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Image, Segment, Header, Table, Button, Icon, Dropdown, Label, Message } from 'semantic-ui-react';
import { fetchActorUsers, selectActorUsers, selectActorUsersStatus } from '../../slices/voiceActorSlice';
import { StatusLabels } from '../../components/statusTools/StatusIndicators';
import { getPriorityColor, getStatusColor } from '../../constants/statusConfig';
import micAndShieldImage from '../../assets/micAndshield.svg';

import { AuthStdFooter } from "../../utils/auth-std-footer";
const VoiceActorAssignments = () => {
  const dispatch = useDispatch();
  const voiceActors = useSelector(selectActorUsers);
  const actorsStatus = useSelector(selectActorUsersStatus);

  // Mock data for books - in a real app, this would come from an API
  const [books, setBooks] = useState([
    {
      id: 1,
      title: 'The Deadly Crocus',
      author: 'Jane Smith',
      status: 'In Progress',
      priority: 'High',
      deadline: '2025-05-15',
      characters: [
        { id: 1, name: 'Protagonist', actorId: null, status: 'Unassigned' },
        { id: 2, name: 'Antagonist', actorId: null, status: 'Unassigned' },
        { id: 3, name: 'Narrator', actorId: null, status: 'Unassigned' }
      ]
    },
    {
      id: 2,
      title: 'Luck of M13',
      author: 'John Doe',
      status: 'New',
      priority: 'Medium',
      deadline: '2025-06-30',
      characters: [
        { id: 4, name: 'Hero', actorId: null, status: 'Unassigned' },
        { id: 5, name: 'Villain', actorId: null, status: 'Unassigned' },
        { id: 6, name: 'Sidekick', actorId: null, status: 'Unassigned' },
        { id: 7, name: 'Narrator', actorId: null, status: 'Unassigned' }
      ]
    }
  ]);

  // Selected book for assignment
  const [selectedBook, setSelectedBook] = useState(null);

  // Fetch voice actors when component mounts
  useEffect(() => {
    if (actorsStatus === 'idle') {
      dispatch(fetchActorUsers());
    }
  }, [actorsStatus, dispatch]);

  // Handle assigning a voice actor to a character
  const handleAssignActor = (bookId, characterId, actorId) => {
    setBooks(books.map(book => {
      if (book.id === bookId) {
        return {
          ...book,
          characters: book.characters.map(character => {
            if (character.id === characterId) {
              return {
                ...character,
                actorId,
                status: actorId ? 'Assigned' : 'Unassigned'
              };
            }
            return character;
          })
        };
      }
      return book;
    }));
  };

  // Get actor name by ID
  const getActorName = (actorId) => {
    if (!actorId) return 'Unassigned';
    const actor = voiceActors.find(actor => actor.uuid === actorId);
    return actor ? `${actor.firstName} ${actor.lastName}` : 'Unknown';
  };

  // Render the book list
  const renderBookList = () => {
    return (
      <Segment style={{ marginBottom: "70px" }}>
        <Header as="h3">Your Books</Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Book Title</Table.HeaderCell>
              <Table.HeaderCell>Author</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Characters</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {books.map(book => (
              <Table.Row key={book.id}>
                <Table.Cell>{book.name}</Table.Cell>
                <Table.Cell>{book.author}</Table.Cell>
                <Table.Cell>
                  <StatusLabels
                    priority={book.priority}
                    status={book.status}
                    deadline={book.deadline}
                    getPriorityColor={getPriorityColor}
                    getStatusColor={getStatusColor}
                  />
                </Table.Cell>
                <Table.Cell>{book.characters.length} characters</Table.Cell>
                <Table.Cell>
                  <Button
                    primary
                    onClick={() => setSelectedBook(book)}
                  >
                    Manage Assignments
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  // Render the character assignment table for a selected book
  const renderCharacterAssignments = () => {
    if (!selectedBook) return null;

    return (
      <Segment>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Header as="h3">
            Character Assignments for "{selectedBook.name}"
            <Header.Subheader>Assign voice actors to each character</Header.Subheader>
          </Header>
          <Button icon labelPosition="left" onClick={() => setSelectedBook(null)}>
            <Icon name="arrow left" />
            Back to Book List
          </Button>
        </div>

        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Character</Table.HeaderCell>
              <Table.HeaderCell>Assigned Voice Actor</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {selectedBook.characters.map(character => (
              <Table.Row key={character.id}>
                <Table.Cell>{character.name}</Table.Cell>
                <Table.Cell>
                  {character.actorId ? (
                    <span>{getActorName(character.actorId)}</span>
                  ) : (
                    <span style={{ color: 'gray' }}>Unassigned</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Label color={character.actorId ? 'green' : 'grey'}>
                    {character.status}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  <Dropdown
                    text={character.actorId ? 'Change Assignment' : 'Assign Voice Actor'}
                    floating
                    labeled
                    button
                    className='icon'
                  >
                    <Dropdown.Menu>
                      <Dropdown.Header content='Select Voice Actor' />
                      {character.actorId && (
                        <Dropdown.Item
                          text="Remove Assignment"
                          onClick={() => handleAssignActor(selectedBook.id, character.id, null)}
                          icon="trash"
                        />
                      )}
                      <Dropdown.Divider />
                      {voiceActors.map(actor => (
                        <Dropdown.Item
                          key={actor.uuid}
                          text={`${actor.firstName} ${actor.lastName}`}
                          onClick={() => handleAssignActor(selectedBook.id, character.id, actor.uuid)}
                          icon={character.actorId === actor.uuid ? 'check' : null}
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  };

  return (
    <div className="listContainer">
      <div style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="heading-img-with-no-gradient">
                <Image src={micAndShieldImage} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Voice Actor Assignments</span>
              <span className="blgListSubTitle">Manage voice actor assignments for your books</span>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {actorsStatus === 'loading' ? (
        <Message icon>
          <Icon name='circle notched' loading />
          <Message.Content>
            <Message.Header>Just a moment</Message.Header>
            Loading voice actors...
          </Message.Content>
        </Message>
      ) : actorsStatus === 'failed' ? (
        <Message negative>
          <Message.Header>Error loading voice actors</Message.Header>
          <p>There was a problem loading the voice actors. Please try again later.</p>
        </Message>
      ) : (
        <>
          {selectedBook ? renderCharacterAssignments() : renderBookList()}
        </>
      )}
      {AuthStdFooter()}
    </div>
  );
};

export default VoiceActorAssignments;
